import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem("token");
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const data = response.data;
    
    // 2FA required: no access_token yet — caller handles the next step
    if (data.requires_2fa || data.requires_2fa_setup) {
      return { twofaChallenge: data };
    }
    
    const { access_token, user: userData } = data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const complete2FALogin = async (temp_token, code) => {
    const response = await axios.post(`${API}/auth/2fa/verify-login`, { temp_token, code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const setup2FA = async (temp_token) => {
    const response = await axios.post(`${API}/auth/2fa/setup`, { temp_token });
    return response.data; // { secret, otpauth_uri, qr_code_data_url }
  };

  const verify2FASetup = async (temp_token, code) => {
    const response = await axios.post(`${API}/auth/2fa/verify-setup`, { temp_token, code });
    const { access_token, user: userData, backup_codes } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return { user: userData, backup_codes };
  };

  const register = async (email, password, name, role = "customer") => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      name,
      role 
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const savedToken = localStorage.getItem("token");
    if (!savedToken) return;
    const response = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${savedToken}` }
    });
    setUser(response.data);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      getAuthHeaders,
      refreshUser,
      complete2FALogin,
      setup2FA,
      verify2FASetup,
      API 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
